"use client";

import Image from "next/image";
import { motion } from "motion/react";
import { useState } from "react";
import { observer } from "mobx-react-lite";
import SplashPill from "../ui/SplashPill";
import store from "@/store/store";
import { getDirecutsImageUrl } from "@/app/utils/getDirectusImageUrl";
import GradientWrapper from "../ui/Gradient";

// Fallback labels, used while the global data is not loaded yet (loading.tsx) or a CMS field is empty.
const splashPills = [
    { field: "splash_pill_one", label: "Nur eine Frage.", tilt: "rotate-[2deg]" },
    { field: "splash_pill_two", label: "Jeden Tag.", tilt: "rotate-[-2deg]" },
    { field: "splash_pill_three", label: "Für dein Team.", tilt: "rotate-[2deg]" },
] as const;

const pillsContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            delayChildren: 0.25,
            staggerChildren: 0.25,
            staggerDirection: 1,
        },
    },
} as const;

const pillItemVariants = {
    hidden: { y: 56, opacity: 0, scale: 0.92 },
    visible: {
        y: 0,
        opacity: 1,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 200,
            damping: 15,
            mass: 0.75,
            bounce: 0.3,
        },
    },
} as const;

type SplashProps = {
    // True once everything needed for the start screen is loaded; then a tap anywhere skips the splash.
    canSkip?: boolean;
    onSkip?: () => void;
};

const Splash = observer(function Splash({ canSkip = false, onSkip }: SplashProps) {
    const [liftUp, setLiftUp] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const logoOverride = store.specialMonthData?.subline_logo_override;
    const logoSrc =
        typeof logoOverride === "string" && logoOverride.trim().length > 0
            ? getDirecutsImageUrl(logoOverride)
            : "/images/NoShitLogoWithSubline.png";

    const isSpecial = store.specialMonthData?.key;
    const globalTranslation = store.globalData?.translations?.[0];

    return (
        <div
            className="relative flex flex-col justify-center items-center gap-9 w-full h-full"
            onClick={canSkip ? onSkip : undefined}
        >
            <span className="flex flex-col justify-center items-center">
                <motion.div
                    key="stage-logo"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onAnimationComplete={() => setLiftUp(true)}
                    exit={{
                        scale: 0,
                        opacity: 0,
                        transition: {
                            type: "tween",
                            duration: 0.28,
                            ease: "easeInOut",
                        },
                    }}
                    transition={{
                        type: "spring",
                        duration: 1,
                        bounce: 0.3,
                    }}
                    className="z-10"
                >
                    <motion.div
                        initial={{ y: 0 }}
                        animate={liftUp ? { y: -30 } : { y: 0 }}
                        transition={{
                            delay: 0.25,
                            type: "spring",
                            duration: 1,
                            bounce: 0.3,
                        }}
                        onAnimationStart={() => {
                            if (liftUp) setReady(true);
                        }}
                    >
                        <Image
                            src={logoSrc}
                            alt="NoShit Logo"
                            width={300}
                            height={200}
                            sizes="300px"
                            priority
                            loading="eager"
                            className="max-w-75 h-auto max-h-50"
                        />
                    </motion.div>
                </motion.div>

                {isSpecial &&
                    <motion.div
                        key={isSpecial}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={
                            ready
                                ? { scale: 1, opacity: 1 }
                                : { scale: 0, opacity: 0 }
                        }
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{
                            delay: 1.25,
                            type: "spring",
                            duration: 0.5,
                            bounce: 0,
                        }}
                        className="inline-flex relative justify-center items-center bg-red-550 px-3 rounded-full h-8 font-16-bold text-red-900 align-middle"
                    >
                        <button
                            type="button"
                            onClick={() => store.open("specialQuestion")}
                            className="z-10 relative flex justify-center items-center font-16-bold leading-[125%] tracking-[0%]">
                            <span>{store.specialMonthData?.translations?.[0]?.special_title}</span>
                        </button>
                        <GradientWrapper gradient="bg-gradient-light-elevation-linear-matt" rounded="rounded-full" />
                    </motion.div>
                }
            </span>

            <motion.div
                key="stage-pills"
                initial={{ opacity: 0 }}
                animate={ready ? { opacity: 1 } : { opacity: 0 }}
                exit={{
                    opacity: 0,
                    transition: {
                        type: "tween",
                        duration: 0.28,
                        ease: "easeInOut",
                    },
                }}
                transition={{
                    duration: 0.2,
                }}
                className="z-10 flex flex-col justify-center items-center px-4 w-screen max-w-150"
            >
                <motion.div
                    className="flex flex-col items-center"
                    variants={pillsContainerVariants}
                    initial="hidden"
                    animate={ready ? "visible" : "hidden"}
                >
                    {splashPills.map((pill, index) => {
                        const label = globalTranslation?.[pill.field]?.trim() || pill.label;
                        return (
                            <motion.div
                                key={pill.field}
                                variants={pillItemVariants}
                                className={index === 0 ? "" : "mt-0"}
                                style={{ zIndex: splashPills.length + index }}
                            >
                                <SplashPill label={label} tilt={pill.tilt} />
                            </motion.div>
                        );
                    })}
                </motion.div>
            </motion.div>
            {canSkip && onSkip &&
                <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    type="button"
                    className="bottom-8 z-20 absolute font-12-bold text-green-800/75"
                >
                    Tippen zum Überspringen
                </motion.button>
            }
        </div>
    );
});

export default Splash;